import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiAlertTriangle } from 'react-icons/fi';
import './LegalPages.css';

const DeleteAccount = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const en = (
    <>
      <div className="delete-warning">
        <FiAlertTriangle size={24} />
        <div>
          <strong>Important:</strong> Account deletion is permanent and cannot be undone. Please read the instructions carefully before proceeding.
        </div>
      </div>

      <h2>Account & Data Deletion</h2>
      <p>Roolink respects your right to delete your account and personal data. Below are the instructions for deleting your account across our services.</p>

      <h2>Before You Delete</h2>
      <p>Please ensure the following before requesting account deletion:</p>
      <ul>
        <li>Download any data, files, or backups you wish to keep</li>
        <li>Cancel all active subscriptions (hosting, domains, etc.)</li>
        <li>Transfer any domains you wish to keep to another registrar</li>
        <li>Clear any outstanding invoices or payments</li>
        <li>Download copies of your invoices for your records</li>
      </ul>

      <h2>Method 1: Self-Service Deletion (Client Portal)</h2>
      <ol>
        <li>Log in to the <a href="https://portal.roolink.net/clientarea.php" target="_blank" rel="noopener noreferrer">Roolink Client Portal</a></li>
        <li>Navigate to <strong>Account Settings</strong> (Hello, Name &gt; My Details)</li>
        <li>Scroll to the bottom of the page</li>
        <li>Click <strong>"Close Account"</strong> or <strong>"Request Account Deletion"</strong></li>
        <li>Confirm your identity by entering your password</li>
        <li>Select the reason for deletion (optional)</li>
        <li>Click <strong>"Confirm Deletion"</strong></li>
      </ol>

      <h2>Method 2: Email Request</h2>
      <p>Send an email to <a href="mailto:info@roolink.net">info@roolink.net</a> with the following:</p>
      <ul>
        <li><strong>Subject:</strong> Account Deletion Request</li>
        <li><strong>Body must include:</strong>
          <ul>
            <li>Your full name as registered on the account</li>
            <li>The email address associated with your account</li>
            <li>Your client ID (found in the client portal)</li>
            <li>Statement: "I request the permanent deletion of my account and all associated data"</li>
          </ul>
        </li>
      </ul>

      <h2>Method 3: Phone Request</h2>
      <p>Call us at <a href="tel:+96550580600" dir="ltr">+965 50580600</a> during business hours to request account deletion. You will need to verify your identity.</p>

      <h2>What Gets Deleted</h2>
      <table className="legal-table">
        <thead>
          <tr>
            <th>Data Type</th>
            <th>Action</th>
            <th>Timeline</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Account profile & personal info</td><td>Permanently deleted</td><td>Within 7 days</td></tr>
          <tr><td>Hosting files & databases</td><td>Permanently deleted</td><td>Within 7 days</td></tr>
          <tr><td>Email accounts & content</td><td>Permanently deleted</td><td>Within 7 days</td></tr>
          <tr><td>Support tickets & chat history</td><td>Permanently deleted</td><td>Within 14 days</td></tr>
          <tr><td>Backup data</td><td>Purged from all systems</td><td>Within 30 days</td></tr>
          <tr><td>Billing records & invoices</td><td>Retained (legal requirement)</td><td>7 years per Kuwait law</td></tr>
          <tr><td>Domain WHOIS history</td><td>Retained by ICANN/registry</td><td>Per ICANN policy</td></tr>
        </tbody>
      </table>

      <h2>Mobile App Data Deletion</h2>
      <p>If you use any Roolink-developed mobile applications:</p>
      <ol>
        <li>Open the app and go to <strong>Settings</strong> &gt; <strong>Account</strong></li>
        <li>Tap <strong>"Delete Account"</strong></li>
        <li>Confirm the deletion</li>
        <li>Uninstall the app from your device</li>
      </ol>
      <p>Alternatively, you can request app data deletion via email at <a href="mailto:info@roolink.net">info@roolink.net</a>.</p>

      <h2>Processing Time</h2>
      <ul>
        <li>Self-service deletion: Processed within <strong>24-48 hours</strong></li>
        <li>Email requests: Processed within <strong>3-5 business days</strong></li>
        <li>Full data purge from all systems: Up to <strong>30 days</strong></li>
      </ul>

      <h2>After Deletion</h2>
      <ul>
        <li>You will receive a confirmation email once your account is deleted</li>
        <li>You will no longer be able to access any services or data</li>
        <li>Any active services will be immediately terminated</li>
        <li>Domains not transferred will expire at the end of their registration period</li>
        <li>You can create a new account at any time, but previous data cannot be recovered</li>
      </ul>

      <h2>Contact</h2>
      <p>For questions about data deletion:</p>
      <ul>
        <li>Email: <a href="mailto:info@roolink.net">info@roolink.net</a></li>
        <li>Phone: <a href="tel:+96550580600" dir="ltr">+965 50580600</a></li>
      </ul>
    </>
  );

  const ar = (
    <>
      <div className="delete-warning">
        <FiAlertTriangle size={24} />
        <div>
          <strong>هام:</strong> حذف الحساب دائم ولا يمكن التراجع عنه. يرجى قراءة التعليمات بعناية قبل المتابعة.
        </div>
      </div>

      <h2>حذف الحساب والبيانات</h2>
      <p>تحترم رولينك حقك في حذف حسابك وبياناتك الشخصية. فيما يلي تعليمات حذف حسابك عبر خدماتنا.</p>

      <h2>قبل الحذف</h2>
      <p>يرجى التأكد مما يلي قبل طلب حذف الحساب:</p>
      <ul>
        <li>تحميل أي بيانات أو ملفات أو نسخ احتياطية ترغب في الاحتفاظ بها</li>
        <li>إلغاء جميع الاشتراكات النشطة (الاستضافة والنطاقات وغيرها)</li>
        <li>نقل أي نطاقات ترغب في الاحتفاظ بها إلى مسجل آخر</li>
        <li>تسوية أي فواتير أو مدفوعات معلقة</li>
        <li>تحميل نسخ من فواتيرك لسجلاتك</li>
      </ul>

      <h2>الطريقة 1: الحذف الذاتي (بوابة العملاء)</h2>
      <ol>
        <li>سجل الدخول إلى <a href="https://portal.roolink.net/clientarea.php" target="_blank" rel="noopener noreferrer">بوابة عملاء رولينك</a></li>
        <li>انتقل إلى <strong>إعدادات الحساب</strong> (مرحباً، الاسم &gt; بياناتي)</li>
        <li>انتقل إلى أسفل الصفحة</li>
        <li>اضغط على <strong>"إغلاق الحساب"</strong> أو <strong>"طلب حذف الحساب"</strong></li>
        <li>أكد هويتك بإدخال كلمة المرور</li>
        <li>اختر سبب الحذف (اختياري)</li>
        <li>اضغط على <strong>"تأكيد الحذف"</strong></li>
      </ol>

      <h2>الطريقة 2: طلب عبر البريد الإلكتروني</h2>
      <p>أرسل بريداً إلكترونياً إلى <a href="mailto:info@roolink.net">info@roolink.net</a> يتضمن:</p>
      <ul>
        <li><strong>الموضوع:</strong> طلب حذف حساب</li>
        <li><strong>يجب أن يتضمن النص:</strong>
          <ul>
            <li>اسمك الكامل كما هو مسجل في الحساب</li>
            <li>عنوان البريد الإلكتروني المرتبط بحسابك</li>
            <li>رقم العميل (موجود في بوابة العملاء)</li>
            <li>عبارة: "أطلب الحذف الدائم لحسابي وجميع البيانات المرتبطة"</li>
          </ul>
        </li>
      </ul>

      <h2>الطريقة 3: طلب هاتفي</h2>
      <p>اتصل بنا على <a href="tel:+96550580600" dir="ltr">+965 50580600</a> خلال ساعات العمل لطلب حذف الحساب. ستحتاج إلى التحقق من هويتك.</p>

      <h2>ما يتم حذفه</h2>
      <table className="legal-table">
        <thead>
          <tr>
            <th>نوع البيانات</th>
            <th>الإجراء</th>
            <th>المدة الزمنية</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>ملف الحساب والمعلومات الشخصية</td><td>حذف دائم</td><td>خلال 7 أيام</td></tr>
          <tr><td>ملفات الاستضافة وقواعد البيانات</td><td>حذف دائم</td><td>خلال 7 أيام</td></tr>
          <tr><td>حسابات البريد الإلكتروني والمحتوى</td><td>حذف دائم</td><td>خلال 7 أيام</td></tr>
          <tr><td>تذاكر الدعم وسجل المحادثات</td><td>حذف دائم</td><td>خلال 14 يوماً</td></tr>
          <tr><td>بيانات النسخ الاحتياطي</td><td>مسح من جميع الأنظمة</td><td>خلال 30 يوماً</td></tr>
          <tr><td>سجلات الفوترة والفواتير</td><td>يتم الاحتفاظ بها (متطلب قانوني)</td><td>7 سنوات وفقاً للقانون الكويتي</td></tr>
          <tr><td>سجل WHOIS للنطاق</td><td>يحتفظ بها ICANN/السجل</td><td>وفقاً لسياسة ICANN</td></tr>
        </tbody>
      </table>

      <h2>حذف بيانات تطبيقات الجوال</h2>
      <p>إذا كنت تستخدم أي تطبيقات جوال مطورة من رولينك:</p>
      <ol>
        <li>افتح التطبيق وانتقل إلى <strong>الإعدادات</strong> &gt; <strong>الحساب</strong></li>
        <li>اضغط على <strong>"حذف الحساب"</strong></li>
        <li>أكد الحذف</li>
        <li>ألغِ تثبيت التطبيق من جهازك</li>
      </ol>
      <p>بدلاً من ذلك، يمكنك طلب حذف بيانات التطبيق عبر البريد الإلكتروني على <a href="mailto:info@roolink.net">info@roolink.net</a>.</p>

      <h2>وقت المعالجة</h2>
      <ul>
        <li>الحذف الذاتي: يتم المعالجة خلال <strong>24-48 ساعة</strong></li>
        <li>طلبات البريد الإلكتروني: يتم المعالجة خلال <strong>3-5 أيام عمل</strong></li>
        <li>المسح الكامل للبيانات من جميع الأنظمة: حتى <strong>30 يوماً</strong></li>
      </ul>

      <h2>بعد الحذف</h2>
      <ul>
        <li>ستتلقى بريداً إلكترونياً للتأكيد بمجرد حذف حسابك</li>
        <li>لن تتمكن بعد ذلك من الوصول إلى أي خدمات أو بيانات</li>
        <li>سيتم إنهاء أي خدمات نشطة فوراً</li>
        <li>النطاقات التي لم تُنقل ستنتهي في نهاية فترة تسجيلها</li>
        <li>يمكنك إنشاء حساب جديد في أي وقت، لكن البيانات السابقة لا يمكن استردادها</li>
      </ul>

      <h2>اتصل بنا</h2>
      <p>للأسئلة حول حذف البيانات:</p>
      <ul>
        <li>البريد الإلكتروني: <a href="mailto:info@roolink.net">info@roolink.net</a></li>
        <li>الهاتف: <a href="tel:+96550580600" dir="ltr">+965 50580600</a></li>
      </ul>
    </>
  );

  return (
    <div className="legal-page">
      <div className="legal-header delete-header">
        <div className="legal-header-bg">
          <div className="legal-shape ls1"></div>
          <div className="legal-shape ls2"></div>
        </div>
        <div className="container">
          <a href="/" className="legal-back"><FiArrowLeft /> {isAr ? 'الرئيسية' : 'Home'}</a>
          <h1>{isAr ? 'حذف الحساب والبيانات' : 'Account & Data Deletion'}</h1>
          <p>Roolink</p>
        </div>
      </div>
      <motion.div
        className="container legal-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {isAr ? ar : en}
      </motion.div>
    </div>
  );
};

export default DeleteAccount;
